import type React from "react";

import { Avatar, Tooltip, type VirtualTableColumn } from "@snapchat/snap-design-system";

import previewInReviewSvg from "@app-assets/svg/preview-in-review.svg";

import { type MyLensesAsset } from "../../gql-generated/types";
import { Flexbox } from "../../shared/components";
import { truncate } from "../../shared/styles";
import { type AssetResponse } from "../types/assets";
import { getAssetStatusMeta } from "../utils/assetStatus";
import { formatDate } from "../utils/formatDate";

interface CellRenderer {
    row: {
        index: number;
    };
    data: AssetResponse[];
}

const getRenderer = (fn: (item?: AssetResponse) => React.JSX.Element) => {
    return ({ row, data }: CellRenderer) => fn(data[row.index]);
};

const imageRegex = new RegExp(/\S+(.*?).(jpg|jpeg|png|gif|JPG|JPEG|PNG|GIF)$/);

const getIconUrl = (file?: MyLensesAsset | null) => {
    if (file?.url && imageRegex.test(file.url)) {
        return file.url;
    }
    return previewInReviewSvg;
};

export const getColumns = (): VirtualTableColumn<AssetResponse>[] => {
    return [
        {
            id: "icon",
            Header: <div className={truncate}>Icon</div>,
            width: 72,
            disableSortBy: true,
            Cell: getRenderer((asset) => (
                <Avatar imageSrc={getIconUrl(asset?.previewsCollection.items[0])} diameter={40} borderWidth={0} />
            )),
        },
        {
            id: "name",
            Header: <div className={truncate}>Name</div>,
            Cell: getRenderer((asset) => {
                return <div className={truncate}>{asset?.name}</div>;
            }),
        },
        {
            // id must match the data field so useTableFilterAndSort can compare rows
            id: "lastUpdated",
            Header: <div className={truncate}>Date Modified</div>,
            width: 150,
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- testing for falsy value
            Cell: getRenderer((asset) => <div>{formatDate(asset?.lastUpdated ?? "")}</div>),
        },
        {
            id: "status",
            Header: <div className={truncate}>Status</div>,
            width: 150,
            Cell: getRenderer((asset) => {
                if (!asset) {
                    return <></>;
                }
                const { rejections } = asset;
                const { name, color, Icon } = getAssetStatusMeta(asset.status);

                if (rejections && rejections.length > 0) {
                    const firstRejection = rejections[0];
                    return (
                        <Tooltip title={firstRejection?.reason} content={firstRejection?.message} placement="top">
                            <Flexbox alignItems="center">
                                <Icon fill={color} />
                                {name}
                            </Flexbox>
                        </Tooltip>
                    );
                }

                return (
                    <Flexbox alignItems="center">
                        <Icon fill={color} />
                        {name}
                    </Flexbox>
                );
            }),
        },
    ];
};
